import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Camera, X, AlertCircle } from "lucide-react";

const MAX_CHARS = 50000;

const FILE_ACCEPT = ".csv,.xlsx,.xls";
const FILE_MIME_ACCEPT =
  "text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/heic,image/webp";

interface Props {
  onSubmitText: (text: string) => void;
  onSubmitFile: (file: File) => void;
  onSubmitImage: (file: File) => void;
  submitting: boolean;
  error: string | null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RosterInputStep({
  onSubmitText,
  onSubmitFile,
  onSubmitImage,
  submitting,
  error,
}: Props) {
  const [text, setText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
  };

  const clearImage = () => {
    setSelectedImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <Tabs defaultValue="text">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="text">
            <FileText className="h-4 w-4 mr-1.5" />
            Paste text
          </TabsTrigger>
          <TabsTrigger value="file">
            <Upload className="h-4 w-4 mr-1.5" />
            Upload file
          </TabsTrigger>
          <TabsTrigger value="photo">
            <Camera className="h-4 w-4 mr-1.5" />
            Upload photo
          </TabsTrigger>
        </TabsList>

        {/* ── Paste text ── */}
        <TabsContent value="text" className="space-y-2 mt-4">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            className="min-h-[200px] font-mono text-sm"
            placeholder={`Marcus Johnson #14 PG 6-2 Class of 2027 marcus@example.com MO\nSarah Williams #22 SG 5-8 2026 (parent: mom@example.com) TX`}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {text.length > MAX_CHARS && (
                <span className="text-red-600 font-medium">
                  Over {MAX_CHARS.toLocaleString()} character limit.{" "}
                </span>
              )}
              {text.length.toLocaleString()} chars
            </span>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => onSubmitText(text)}
              disabled={!text.trim() || text.length > MAX_CHARS || submitting}
            >
              Parse roster
            </Button>
          </div>
        </TabsContent>

        {/* ── Upload file ── */}
        <TabsContent value="file" className="space-y-3 mt-4">
          <input
            ref={fileRef}
            type="file"
            accept={`${FILE_ACCEPT},${FILE_MIME_ACCEPT}`}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setSelectedFile(f);
            }}
          />
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary-300 transition-colors"
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">
              Click to select a file or drag and drop
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              CSV, XLSX, or XLS only (no PDFs)
            </p>
          </div>
          {selectedFile && (
            <div className="flex items-center justify-between bg-muted/50 rounded-md p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{selectedFile.name}</span>
                <span className="text-xs text-muted-foreground">
                  ({formatFileSize(selectedFile.size)})
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFile(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={() => selectedFile && onSubmitFile(selectedFile)}
              disabled={!selectedFile || submitting}
            >
              Parse roster
            </Button>
          </div>
        </TabsContent>

        {/* ── Upload photo ── */}
        <TabsContent value="photo" className="space-y-3 mt-4">
          <input
            ref={imageRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImageSelect(f);
            }}
          />
          <div
            onClick={() => imageRef.current?.click()}
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary-300 transition-colors"
          >
            <Camera className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">
              Click to select a photo or drag and drop
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              JPEG, PNG, WebP, or HEIC
            </p>
          </div>
          {selectedImage && (
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-muted/50 rounded-md p-3">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {selectedImage.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({formatFileSize(selectedImage.size)})
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={clearImage}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-48 rounded-md border object-contain"
                />
              )}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={() => selectedImage && onSubmitImage(selectedImage)}
              disabled={!selectedImage || submitting}
            >
              Parse roster
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
